import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Sparkles, Clock, Shield,
  Mic, Brain, Columns, FileDown, Menu, X,
} from 'lucide-react';
import { LogoBicolor } from '../components/LogoBicolor';
import './landing-design.css';

// ── Institutional links (cross-domain bridge) ──────────────────────────
const INSTITUTIONAL_BASE = 'https://akaliai.com.br';
const INSTITUTIONAL_PRIVACY = `${INSTITUTIONAL_BASE}/privacidade`;
const INSTITUTIONAL_TERMS = `${INSTITUTIONAL_BASE}/termos`;
const INSTITUTIONAL_LGPD = `${INSTITUTIONAL_BASE}/lgpd`;

// ── Step config (preserves all copy verbatim) ──────────────────────────
const STEPS = [
  { n: 1, icon: Mic, title: 'Grave ou Digite',  body: 'Fale livremente durante ou após o atendimento, ou digite as informações da consulta. Funciona por voz ou texto.' },
  { n: 2, icon: Brain, title: 'IA Processa',     body: 'A IA transcreve o áudio em PT-BR. O modelo de linguagem gera o prontuário estruturado. O sistema enriquece com o histórico do paciente.' },
  { n: 3, icon: Columns, title: 'Revise',         body: 'Tela de revisão em duas colunas: transcrição original à esquerda, prontuário gerado pela IA à direita. Compare e edite com facilidade.' },
  { n: 4, icon: Shield, title: 'Aprove',          body: 'Aprovação seção por seção para conformidade com o COFFITO. Nenhuma informação é salva sem a revisão e aprovação do profissional.' },
  { n: 5, icon: FileDown, title: 'Salve e Exporte', body: 'Gere o PDF do prontuário para o paciente, encaminhamento ou convênio. Documento salvo com log de auditoria e rastreabilidade completa.' },
];

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="akali-landing">
      {/* ─── Navbar ────────────────────────────────────────────────── */}
      <header className="lp-navbar sticky top-0 z-50" data-testid="lp-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center gap-6">
            {/* Institutional bridge — discreet back link */}
            <a
              href={INSTITUTIONAL_BASE}
              className="hidden md:inline-flex items-center lp-mono lp-text-muted hover:lp-text-primary transition-colors"
              style={{ fontSize: '12px' }}
              data-testid="lp-institutional-back"
            >
              ← akaliai.com.br
            </a>

            {/* Brand */}
            <LogoBicolor size={20} symbolSize={28} />

            {/* Desktop actions */}
            <div className="hidden md:flex gap-3 items-center">
              <Link to="/login">
                <button className="lp-btn-ghost" data-testid="header-login-btn">Entrar</button>
              </Link>
              <Link to="/register">
                <button
                  className="lp-btn-primary"
                  style={{ height: 44, fontSize: 15, padding: '0 20px' }}
                  data-testid="header-signup-btn"
                >
                  Começar Grátis
                </button>
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden lp-text-primary p-2"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Abrir menu"
              data-testid="lp-mobile-menu-btn"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile drawer */}
          {menuOpen && (
            <div className="md:hidden mt-4 flex flex-col gap-3 pb-2" data-testid="lp-mobile-menu">
              <a
                href={INSTITUTIONAL_BASE}
                className="lp-mono lp-text-muted"
                style={{ fontSize: '12px' }}
              >
                ← akaliai.com.br
              </a>
              <Link to="/login" onClick={() => setMenuOpen(false)}>
                <button className="lp-btn-secondary w-full" style={{ height: 44, fontSize: 15 }}>
                  Entrar
                </button>
              </Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>
                <button className="lp-btn-primary w-full" style={{ height: 44, fontSize: 15 }}>
                  Começar Grátis
                </button>
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* ─── Hero ──────────────────────────────────────────────────── */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-testid="lp-hero">
        <div className="lp-hero-glow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 lp-card" style={{ background: 'rgba(241, 136, 89, 0.08)', border: '1px solid rgba(241, 136, 89, 0.30)' }}>
              <Sparkles className="h-4 w-4 lp-text-accent" />
              <span className="lp-eyebrow" style={{ color: 'var(--color-accent)' }}>Inteligência Artificial para Fisioterapeutas</span>
            </div>

            <h1
              className="lp-display lp-text-primary mb-6"
              style={{ fontSize: 'clamp(40px, 6vw, 64px)', fontWeight: 600, lineHeight: 1.05 }}
            >
              Documentação clínica inteligente em segundos
            </h1>

            <p className="lp-body lp-text-secondary mb-8 max-w-2xl" style={{ fontSize: 20, lineHeight: 1.6 }}>
              Transforme suas consultas em documentação profissional seguindo os protocolos do COFFITO. Mais tempo para cuidar, menos tempo para documentar.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <button className="lp-btn-primary group" data-testid="hero-cta-btn">
                  Começar Agora Grátis
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              </Link>
              <Link to="/login">
                <button className="lp-btn-secondary" data-testid="hero-login-btn">Fazer Login</button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6 mt-8 lp-text-muted lp-body" style={{ fontSize: 14 }}>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 lp-text-accent" />
                <span>Sem cartão de crédito</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 lp-text-accent" />
                <span>Teste grátis 15 consultas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How it works ──────────────────────────────────────────── */}
      <section className="lp-bg-surface py-20" id="como-funciona" data-testid="lp-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="lp-eyebrow mb-3 block">Processo</span>
            <h2 className="lp-display lp-text-primary mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 600 }}>
              Como funciona
            </h2>
            <p className="lp-body lp-text-secondary max-w-2xl mx-auto" style={{ fontSize: 18 }}>
              Cinco passos simples para documentar suas consultas de forma profissional e em conformidade com o COFFITO
            </p>
          </div>

          {/* Desktop: 5-up grid */}
          <div className="hidden lg:grid grid-cols-5 gap-6 relative">
            <div className="absolute top-8 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, var(--color-mid-purple) 50%, transparent 100%)' }} />
            {STEPS.map(({ n, icon: Icon, title, body }) => (
              <div key={n} className="flex flex-col items-center text-center">
                <div className="relative z-10 mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center lp-card" style={{ background: 'var(--color-elevated)' }}>
                    <Icon className="h-7 w-7 lp-text-accent" />
                  </div>
                  <div
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white lp-mono"
                    style={{ background: 'var(--color-accent)', fontSize: 12, fontWeight: 500 }}
                  >
                    {n}
                  </div>
                </div>
                <h3 className="lp-display lp-text-primary mb-2" style={{ fontSize: 18, fontWeight: 600 }}>{title}</h3>
                <p className="lp-body lp-text-secondary" style={{ fontSize: 14, lineHeight: 1.6 }}>{body}</p>
              </div>
            ))}
          </div>

          {/* Mobile: stacked cards */}
          <div className="lg:hidden space-y-4">
            {STEPS.map(({ n, icon: Icon, title, body }) => (
              <div key={n} className="lp-card p-5">
                <div className="flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--color-elevated)', border: '1px solid var(--color-mid-purple)' }}>
                      <Icon className="h-6 w-6 lp-text-accent" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white lp-mono" style={{ background: 'var(--color-accent)', fontSize: 11, fontWeight: 500 }}>
                      {n}
                    </div>
                  </div>
                  <div>
                    <h3 className="lp-display lp-text-primary mb-2" style={{ fontSize: 18, fontWeight: 600 }}>{title}</h3>
                    <p className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Benefits ──────────────────────────────────────────────── */}
      <section className="lp-bg-deep py-20" data-testid="lp-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="lp-eyebrow mb-3 block">Benefícios</span>
              <h2 className="lp-display lp-text-primary mb-6" style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 600, lineHeight: 1.15 }}>
                Mais tempo para o que realmente importa: seus pacientes
              </h2>
              <p className="lp-body lp-text-secondary mb-8" style={{ fontSize: 18, lineHeight: 1.6 }}>
                Economize até 40 minutos por consulta em documentação. Nossa IA entende fisioterapia e segue os protocolos do COFFITO.
              </p>

              <div className="space-y-4">
                <BenefitRow icon={Clock} title="Economize tempo" body="Reduza o tempo de documentação em até 80%" />
                <BenefitRow icon={Shield} title="Conformidade garantida" body="Documentação seguindo padrões COFFITO e CIF" />
                <BenefitRow icon={Sparkles} title="IA especializada" body="Treinada especificamente para fisioterapia brasileira" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 rounded-2xl blur-3xl" style={{ background: 'radial-gradient(circle, rgba(241,136,89,0.15) 0%, transparent 70%)' }} />
              <div className="relative lp-card p-8">
                <div className="space-y-4">
                  <DocPreviewCard color="var(--color-accent)" label="Anamnese Completa" body="Queixa principal, HDA, histórico médico..." />
                  <DocPreviewCard color="var(--color-mid-purple)" label="Diagnóstico Funcional CIF" body="Classificação CIF completa e estruturada" />
                  <DocPreviewCard color="var(--color-accent)" label="Plano de Tratamento" body="Faseado com objetivos SMART e técnicas" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pricing ───────────────────────────────────────────────── */}
      <section className="lp-bg-surface py-20" data-testid="lp-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="lp-eyebrow mb-3 block">Planos</span>
            <h2 className="lp-display lp-text-primary mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 600 }}>
              Planos para cada necessidade
            </h2>
            <p className="lp-body lp-text-secondary" style={{ fontSize: 18 }}>
              Comece grátis e evolua conforme sua prática cresce
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter */}
            <PricingCard
              name="Starter"
              price="Grátis"
              tagline="Para começar a experimentar"
              features={['15 consultas/mês', 'Gravação e transcrição', 'Documentação com IA']}
              cta="Começar Grátis"
              ctaTestId="pricing-starter-btn"
              footnote="Sem cartão de crédito"
            />
            {/* Solo (featured) */}
            <PricingCard
              featured
              name="Solo"
              price="R$ 129"
              priceSuffix="/mês"
              tagline="Para profissionais autônomos"
              subPrice="ou R$ 103,20/mês no anual (-20%)"
              features={['80 consultas/mês', 'Exportação PDF com logo', 'Prompts personalizados', 'Suporte por email (48h)']}
              cta="Assinar Solo"
              ctaTestId="pricing-solo-btn"
            />
            {/* Clínica */}
            <PricingCard
              name="Clínica"
              price="R$ 99"
              priceSuffix="/assento/mês"
              tagline="Para clínicas com equipes"
              subPrice="Mínimo 3 assentos • R$ 297/mês"
              features={['Tudo do Solo × assentos', 'Dashboard administrativo', 'Relatórios consolidados', 'Suporte prioritário (24h)']}
              cta="Assinar Clínica"
              ctaTestId="pricing-clinica-btn"
            />
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─────────────────────────────────────────────── */}
      <section className="lp-bg-deep py-20" data-testid="lp-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="lp-display lp-text-primary mb-6" style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 600 }}>
            Pronto para transformar sua prática?
          </h2>
          <p className="lp-body lp-text-secondary mb-8 max-w-2xl mx-auto" style={{ fontSize: 18 }}>
            Junte-se a centenas de fisioterapeutas que já economizam horas de documentação toda semana
          </p>
          <Link to="/register">
            <button className="lp-btn-primary group" data-testid="final-cta-btn">
              Começar Agora Grátis
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </Link>
        </div>
      </section>

      {/* ─── Footer ────────────────────────────────────────────────── */}
      <footer className="lp-bg-surface py-12" style={{ borderTop: '1px solid var(--color-glass-border)' }} data-testid="lp-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div className="flex flex-col items-start gap-4">
              <LogoBicolor size={20} symbolSize={28} />
              <p className="lp-mono lp-text-muted max-w-md" style={{ fontSize: 11, lineHeight: 1.7 }}>
                Akali AI é um produto operado por<br />
                Fisiopilates Barra Mansa Ltda — CNPJ 49.797.203/0001-40
              </p>
            </div>

            <div className="flex flex-col md:items-end gap-3">
              <div className="flex flex-wrap items-center lp-body" style={{ fontSize: 14 }}>
                <a href={INSTITUTIONAL_PRIVACY} className="lp-text-secondary hover:lp-text-primary transition-colors" data-testid="footer-privacy">Privacidade</a>
                <span className="lp-footer-sep">|</span>
                <a href={INSTITUTIONAL_TERMS} className="lp-text-secondary hover:lp-text-primary transition-colors" data-testid="footer-terms">Termos</a>
                <span className="lp-footer-sep">|</span>
                <a href={INSTITUTIONAL_LGPD} className="lp-text-secondary hover:lp-text-primary transition-colors" data-testid="footer-lgpd">LGPD</a>
              </div>
              <p className="lp-body lp-text-muted" style={{ fontSize: 13 }}>© 2026 Akali AI</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Sub-components (kept local to this file) ──────────────────────────

const BenefitRow = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(241, 136, 89, 0.12)', border: '1px solid rgba(241, 136, 89, 0.25)' }}>
      <Icon className="h-5 w-5 lp-text-accent" />
    </div>
    <div>
      <h3 className="lp-display lp-text-primary mb-1" style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
      <p className="lp-body lp-text-secondary" style={{ fontSize: 15 }}>{body}</p>
    </div>
  </div>
);

const DocPreviewCard = ({ color, label, body }) => (
  <div className="lp-card p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="lp-mono lp-text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{label}</span>
    </div>
    <p className="lp-body lp-text-primary" style={{ fontSize: 14 }}>{body}</p>
  </div>
);

const PricingCard = ({ featured, name, price, priceSuffix, tagline, subPrice, features, cta, ctaTestId, footnote }) => (
  <div className={`lp-card p-8 relative ${featured ? 'lp-card-featured' : ''}`} data-testid={`pricing-${name.toLowerCase()}-card`}>
    {featured && (
      <div
        className="absolute -top-3 left-1/2 -translate-x-1/2 lp-mono"
        style={{
          background: 'var(--color-accent)',
          color: '#fff',
          padding: '4px 12px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
        }}
      >
        Mais escolhido
      </div>
    )}
    <h3 className="lp-display lp-text-primary mb-2" style={{ fontSize: 22, fontWeight: 600 }}>{name}</h3>
    <div className="lp-display lp-text-primary mb-4" style={{ fontSize: 36, fontWeight: 600 }}>
      {price}
      {priceSuffix && <span className="lp-body lp-text-muted" style={{ fontSize: 16, fontWeight: 400 }}>{priceSuffix}</span>}
    </div>
    <p className="lp-body lp-text-secondary mb-1" style={{ fontSize: 15 }}>{tagline}</p>
    {subPrice && <p className="lp-body lp-text-muted mb-6" style={{ fontSize: 13 }}>{subPrice}</p>}
    {!subPrice && <div className="mb-6" />}
    <ul className="space-y-3 mb-8">
      {features.map((f, i) => (
        <li key={i} className="lp-body lp-text-secondary flex items-start gap-2" style={{ fontSize: 15 }}>
          <CheckCircle className="h-5 w-5 lp-text-accent flex-shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Link to="/register">
      <button
        className={featured ? 'lp-btn-primary w-full' : 'lp-btn-secondary w-full'}
        style={{ height: 48, fontSize: 16 }}
        data-testid={ctaTestId}
      >
        {cta}
      </button>
    </Link>
    {footnote && (
      <p className="lp-body lp-text-muted text-center mt-2" style={{ fontSize: 12 }}>{footnote}</p>
    )}
  </div>
);
